
import React, { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useFavorites } from "@/hooks/useFavorites";
import { Star } from "lucide-react";
import { PuttSituation } from "@/types/golf";
import { useTranslation } from "react-i18next";

interface ApproachesFavoritesProps {
  onSelectTest: (test: PuttSituation) => void;
  situations: PuttSituation[][] | PuttSituation[]; // Accept either format
  refreshAfterToast?: () => void;
}

const ApproachesFavorites: React.FC<ApproachesFavoritesProps> = ({ 
  onSelectTest, 
  situations,
  refreshAfterToast 
}) => {
  const { t } = useTranslation('training');
  const { favorites, isLoading, refreshFavorites, removeFavorite } = useFavorites();
  
  // Map approach IDs to translation keys
  const translationKeyMap: Record<number, string> = {
    7: "liftedApproaches",
    8: "longChips",
    9: "shortChips",
    10: "wedging50m",
    11: "wedging30m",
    12: "shortBunker",
    13: "longBunker",
    14: "midDistanceApproaches",
    15: "roughApproaches",
  };
  
  const getSituationName = (situation: PuttSituation) => {
    const exerciseKey = translationKeyMap[situation.id];
    return exerciseKey ? t(`approaches.exercises.${exerciseKey}.name`) : situation.name;
  };
  
  const getSituationDescription = (situation: PuttSituation) => {
    const exerciseKey = translationKeyMap[situation.id];
    return exerciseKey ? t(`approaches.exercises.${exerciseKey}.description`) : situation.description;
  };
  
  // Always refresh favorites when component mounts
  useEffect(() => {
    console.log("ApproachesFavorites: Refreshing favorites on mount");
    refreshFavorites();
  }, [refreshFavorites]);
  
  // Filter to only get approach favorites
  const approachFavorites = favorites.filter(fav => fav.test_type === "approach" || fav.test_type === "approaches");
  
  console.log("ApproachesFavorites - All favorites:", favorites);
  console.log("ApproachesFavorites - Approach favorites:", approachFavorites);
  
  // Flatten situations array to make searching easier, regardless of input format
  const allSituations = Array.isArray(situations[0]) 
    ? (situations as PuttSituation[][]).flat() 
    : (situations as PuttSituation[]);
  
  // Get situation details by ID
  const getSituationDetails = (testId: string) => {
    const id = parseInt(testId, 10);
    return allSituations.find(situation => situation.id === id);
  };
  
  if (isLoading) {
    return (
      <Card className="mb-8">
        <CardHeader className="pb-3">
          <CardTitle className="text-xl flex items-center">
            <Star className="h-5 w-5 mr-2 text-amber-500 fill-amber-500" />
            {t('approaches.favorites.title', 'Mes exercices d\'approches favoris')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500">{t('approaches.favorites.loading', 'Chargement de vos favoris...')}</p>
        </CardContent>
      </Card>
    );
  }
  
  if (approachFavorites.length === 0) {
    return (
      <Card className="mb-8">
        <CardHeader className="pb-3">
          <CardTitle className="text-xl flex items-center">
            <Star className="h-5 w-5 mr-2 text-amber-500 fill-amber-500" />
            {t('approaches.favorites.title', 'Mes exercices d\'approches favoris')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500">{t('approaches.favorites.noFavorites', 'Vous n\'avez pas encore d\'exercices d\'approches favoris.')}</p>
          <p className="text-gray-500 text-sm mt-2">
            {t('approaches.favorites.addToFavorites', 'Cliquez sur l\'étoile à côté d\'un exercice pour l\'ajouter à vos favoris.')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-8">
      <CardHeader className="pb-3">
        <CardTitle className="text-xl flex items-center">
          <Star className="h-5 w-5 mr-2 text-amber-500 fill-amber-500" />
          {t('approaches.favorites.title', 'Mes exercices d\'approches favoris')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {approachFavorites.map((favorite) => {
            const situationDetails = getSituationDetails(favorite.test_id);
            if (!situationDetails) return null;
            
            return (
              <div 
                key={favorite.id}
                className="cursor-pointer border rounded-md p-4 hover:bg-gray-50 transition-colors flex flex-col space-y-2 relative"
                onClick={() => {
                  onSelectTest(situationDetails);
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFavorite(favorite.id);
                    if (refreshAfterToast) {
                      refreshAfterToast();
                    }
                  }}
                  className="absolute top-2 right-2 p-1 hover:bg-gray-200 rounded-full transition-colors"
                  aria-label="Retirer des favoris"
                >
                  <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                </button>
                <div className="flex items-center w-full pr-8">
                  <img 
                    src={situationDetails.image} 
                    alt={getSituationName(situationDetails)} 
                    className="w-12 h-12 object-cover rounded-md mr-3"
                  />
                  <span className="font-medium">{getSituationName(situationDetails)}</span>
                </div>
                <p className="text-xs text-gray-500 w-full line-clamp-2">{getSituationDescription(situationDetails)}</p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ApproachesFavorites;
